import { FolderOpen, Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { CardsHeader } from "../components/CardsHeader";
import { CreatePocketModal } from "../components/CreatePocketModal";
import { PocketRow } from "../components/PocketRow";
import { resolvePocketName } from "../constants/pockets.constants";
import { PocketsService } from "../services/pocketsService";
import type { Pocket, PocketsApiResponse } from "../types/pockets-api.types";

const PAGE_SIZE = 24;

const matchesQuery = (pocket: Pocket, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return resolvePocketName(pocket).toLowerCase().includes(q);
};

const sumPocketBalance = (pockets: Pocket[]): number =>
  pockets.reduce((acc, p) => {
    const v = typeof p.balance === "number" ? p.balance : parseFloat(p.balance as string) || 0;
    return acc + v;
  }, 0);

const formatUSD = (raw: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

export const PocketsPage = () => {
  const { t } = useTranslations("cards");
  const { t: tGlobal } = useTranslations("global");
  const navigate = useNavigate();

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_tarjetas") },
      { label: tGlobal("sidebar_menu_pockets"), to: "/app/cards/pockets" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);

  const [pockets, setPockets] = useState<Pocket[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const requestSeqRef = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await PocketsService.fetchPockets({
        limit: PAGE_SIZE,
        skip: 0,
        withAggregates: true,
      });
      if (seq !== requestSeqRef.current) return;
      const data = response.data as PocketsApiResponse;
      const list = Array.isArray(data?.data) ? data.data : [];
      setPockets(list);
      const nextTotal = typeof data?.total === "number" ? data.total : list.length;
      setTotal(nextTotal);
      setHasMore(typeof data?.hasMore === "boolean" ? data.hasMore : list.length < nextTotal);
    } catch (err: unknown) {
      if (seq !== requestSeqRef.current) return;
      const message = HttpApiService.getErrorMessage(err) || t("pockets_error_load");
      setError(message);
      colocaToast.error(message);
      setPockets([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      if (seq === requestSeqRef.current) setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    const seq = ++requestSeqRef.current;
    setIsLoadingMore(true);
    try {
      const response = await PocketsService.fetchPockets({
        limit: PAGE_SIZE,
        skip: pockets.length,
        withAggregates: true,
      });
      if (seq !== requestSeqRef.current) return;
      const data = response.data as PocketsApiResponse;
      const list = Array.isArray(data?.data) ? data.data : [];
      setPockets((prev) => [...prev, ...list]);
      const nextTotal = typeof data?.total === "number" ? data.total : pockets.length + list.length;
      setTotal(nextTotal);
      setHasMore(
        typeof data?.hasMore === "boolean" ? data.hasMore : pockets.length + list.length < nextTotal,
      );
    } finally {
      if (seq === requestSeqRef.current) setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, pockets.length]);

  const totalBalance = useMemo(() => sumPocketBalance(pockets), [pockets]);
  const totalCards = useMemo(() => pockets.reduce((acc, p) => acc + (p.cards_count ?? 0), 0), [pockets]);
  const totalSpend = useMemo(() => pockets.reduce((acc, p) => acc + (p.month_spend_usd ?? 0), 0), [pockets]);

  const filtered = useMemo(() => pockets.filter((p) => matchesQuery(p, query)), [pockets, query]);

  const handleOpen = useCallback((id: string) => navigate(`/app/cards/pockets/${id}`), [navigate]);

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <CardsHeader
        activeTabId="pockets"
        actions={
          <ColocaButton variant="primary" icon={Plus} onClick={() => setCreateOpen(true)}>
            {t("pocket_new_button")}
          </ColocaButton>
        }
      />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("pockets_stats_total")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{total}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("pockets_stats_total_balance")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{formatUSD(totalBalance)}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("pockets_stats_cards_linked")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{totalCards}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            {t("pockets_stats_month_spend")}
          </div>
          <div className="mt-1 text-2xl font-semibold text-foreground">{formatUSD(totalSpend)}</div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold leading-tight text-foreground">
              {t("pockets_list_section_title")}
            </h2>
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              {filtered.length} {t("cards_list_of_total")} {total} {t("cards_list_results_label")}
            </p>
          </div>
          <ColocaSearchInput
            value={query}
            onValueChange={setQuery}
            placeholder={t("pockets_search_placeholder")}
            className="sm:w-[260px]"
          />
        </div>

        {error ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : isLoading && pockets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <ColocaEmpty
            icon={FolderOpen}
            title={t("pockets_empty_title")}
            description={t("pockets_empty_description")}
          />
        ) : (
          <>
            <div className="space-y-2">
              {filtered.map((pocket) => (
                <PocketRow key={pocket.id} pocket={pocket} onOpen={handleOpen} />
              ))}
            </div>

            {hasMore && !query.trim() && (
              <div className="flex justify-center pt-2">
                <ColocaButton variant="default" onClick={handleLoadMore} loading={isLoadingMore}>
                  {isLoadingMore ? t("cards_loading_more") : t("cards_load_more")}
                </ColocaButton>
              </div>
            )}
          </>
        )}
      </section>

      <CreatePocketModal open={createOpen} onOpenChange={setCreateOpen} onCreated={() => reload()} />
    </div>
  );
};
